"use client";

import { useState, type ReactNode } from "react";
import { ChevronDown, MapPin } from "lucide-react";
import { Typography } from "./Typography";
import { TruncatedField } from "./TruncatedField";
import { cn } from "@/lib/utils/cn";

interface LocationAccordionProps {
  locationNumber: number | null;
  address?: string;
  descriptionOfOperations?: string;
  natureOfBusiness?: string;
  /** Extra elements rendered at the right end of the header (badges, counts, etc.) */
  headerRight?: ReactNode;
  defaultOpen?: boolean;
  children: ReactNode;
  /** Click handler for the address field (highlights source in PDF) */
  onAddressClick?: () => void;
  /** Click handler for the Operations (description_of_operations) field */
  onDescriptionClick?: () => void;
  /** Click handler for the Nature of Business field */
  onNatureOfBusinessClick?: () => void;
  /**
   * Controlled open state (BAR-238). Pass `isOpen` **and** `onToggle` together to
   * let a parent own expansion — needed for "Expand/Collapse All" across a list of
   * locations, which an accordion holding its own state cannot support.
   *
   * Omit both and it stays uncontrolled, seeded by `defaultOpen`. That is the
   * default on purpose: five existing consumers rely on it and none of them
   * should have to change.
   */
  isOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
  /**
   * Render the Operations / Nature of Business strip. Default `true`.
   *
   * Bar-tavern locations carry neither field in their editor — the strip would be
   * two permanently empty columns, one of them showing a red "required" warning
   * for data that premises simply do not have there.
   */
  showOperations?: boolean;
  /**
   * Secondary text after the location number — the mockup's "DBA · address" line.
   * Rendered instead of `address` when supplied.
   */
  secondaryLabel?: ReactNode;
}

export function LocationAccordion({
  locationNumber,
  address,
  descriptionOfOperations,
  natureOfBusiness,
  headerRight,
  defaultOpen = false,
  children,
  onAddressClick,
  onDescriptionClick,
  onNatureOfBusinessClick,
  isOpen: controlledIsOpen,
  onToggle,
  showOperations = true,
  secondaryLabel,
}: LocationAccordionProps) {
  const [uncontrolledIsOpen, setUncontrolledIsOpen] = useState(defaultOpen);

  // Controlled only when `isOpen` is actually supplied. Checking the prop rather
  // than `onToggle` means a consumer can pass a toggle handler purely to observe
  // clicks while still letting the accordion own its state.
  const isControlled = controlledIsOpen !== undefined;
  const isOpen = isControlled ? controlledIsOpen : uncontrolledIsOpen;

  const toggle = () => {
    const next = !isOpen;
    if (!isControlled) setUncontrolledIsOpen(next);
    onToggle?.(next);
  };

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
      <div
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggle();
          }
        }}
        className="w-full flex items-center flex-wrap gap-2 px-4 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
      >
        <ChevronDown
          className={cn("h-4 w-4 text-gray-400 transition-transform", isOpen && "rotate-180")}
        />
        <MapPin className="h-3.5 w-3.5 text-gray-500" />
        <Typography as="span" variant="body" weight="semibold">
          Location {locationNumber ?? "—"}
        </Typography>
        {secondaryLabel ? (
          <span className="min-w-0 flex-shrink">{secondaryLabel}</span>
        ) : (
          address && (
            <Typography
              as="span"
              variant="body"
              color="light"
              truncate
              className={onAddressClick ? "hover:text-blue-600 hover:underline" : ""}
              onClick={onAddressClick ? (e) => { e.stopPropagation(); onAddressClick(); } : undefined}
              role={onAddressClick ? "button" : undefined}
              tabIndex={onAddressClick ? 0 : undefined}
              onKeyDown={onAddressClick ? (e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); onAddressClick(); } } : undefined}
            >
              {address}
            </Typography>
          )
        )}
        {headerRight && (
          <span className="ml-auto flex items-center gap-2 flex-shrink-0">
            {headerRight}
          </span>
        )}
      </div>

      {isOpen && (
        <div className="px-4 py-3">
          {showOperations && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 mb-4 pb-3 border-b border-gray-100">
              <TruncatedField
                label="Operations"
                value={descriptionOfOperations}
                onClick={descriptionOfOperations ? onDescriptionClick : undefined}
              />
              <TruncatedField
                label="Nature of Business"
                value={natureOfBusiness}
                required
                onClick={natureOfBusiness ? onNatureOfBusinessClick : undefined}
              />
            </div>
          )}
          {children}
        </div>
      )}
    </div>
  );
}
